import React, { createContext, useContext, ReactNode } from 'react';
import { useVideoRingNotifications } from '@/hooks/useVideoRingNotifications';
import type { IncomingRing, ActiveDailyCall } from '@/hooks/useVideoRingNotifications';

interface VideoRingNotificationsContextType {
  incomingRing: IncomingRing | null;
  acceptRing: () => Promise<void>;
  rejectRing: () => Promise<void>;
  isProcessing: boolean;
  activeDailyCall: ActiveDailyCall | null;
  clearActiveDailyCall: () => void;
  openDailyCall: (call: ActiveDailyCall) => void;
}

const VideoRingNotificationsContext = createContext<VideoRingNotificationsContextType | undefined>(undefined);
const noopAsync = async () => {};
const fallbackContext: VideoRingNotificationsContextType = {
  incomingRing: null,
  acceptRing: noopAsync,
  rejectRing: noopAsync,
  isProcessing: false,
  activeDailyCall: null,
  clearActiveDailyCall: () => {},
  openDailyCall: () => {},
};
let hasWarnedMissingProvider = false;

export function VideoRingNotificationsProvider({ children }: { children: ReactNode }) {
  const value = useVideoRingNotifications();

  return (
    <VideoRingNotificationsContext.Provider value={value}>
      {children}
    </VideoRingNotificationsContext.Provider>
  );
}

export function useVideoRingNotificationsContext() {
  const context = useContext(VideoRingNotificationsContext);
  if (context === undefined) {
    if (import.meta.env.DEV && !hasWarnedMissingProvider) {
      hasWarnedMissingProvider = true;
      console.warn('useVideoRingNotificationsContext used without VideoRingNotificationsProvider; using safe fallback');
    }
    return fallbackContext;
  }
  return context;
}
